import { useEffect, useState } from "react";

import { Stack } from "@mui/material";

import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import {
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { ValidationErrorProvider } from "@/contexts/ValidationErrorContext";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { Lesson, UserRole } from "@/types";
import { downloadLessonNotesAsPDF, formatDateForFilename } from "@/utils/pdfDownload";

import useVideoContext from "../../hooks/useVideoContext";
import GlobalValidationError from "../GlobalValidationError";
import Sidebar, { SidebarState } from "../Sidebar";
import TimerSnackbar from "../TimerSnackbar";
import VideoArea from "../VideoArea";
import VideoControls from "../VideoControls";

import ConferenceHeader from "./ConferenceHeader";

const Conference = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSandbox =
    searchParams.get("sandbox") === "true" &&
    import.meta.env.VITE_PUBLIC_STAGE !== "production";
  const { roomToken } = location.state || { roomToken: "mock-token" };
  const { lessonId } = useParams<{ lessonId: string }>();
  const [lessonInfo, setLessonInfo] = useState<Lesson | null>(null);
  const [isWhiteboardOpen, setIsWhiteboardOpen] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const { authenticatedAxios, userRole } = useFirebaseContext();
  const [sidebarState, setSidebarState] = useState(SidebarState.STANDARD);
  const [userWantsToDisconnect, setUserWantsToDisconnect] = useState(false);
  const blocker = useBlocker(() => !userWantsToDisconnect);
  const navigate = useNavigate();
  const db = getFirestore();

  const onProceed = () => {
    if (!lessonInfo) return;
    authenticatedAxios.post(`/lessons/start/${lessonId}`).catch((err) => {
      console.error(err);
    });
  };

  const userName =
    userRole === UserRole.Tutor
      ? lessonInfo?.studentName
      : lessonInfo?.tutorName;
  const userPhoto =
    userRole === UserRole.Tutor
      ? lessonInfo?.studentPhotoUrl
      : lessonInfo?.tutorPhotoUrl;
  const userId =
    userRole === UserRole.Tutor ? lessonInfo?.studentId : lessonInfo?.tutorId;

  useEffect(() => {
    if (!lessonId) return;
    const unsubscribe = onSnapshot(
      doc(db, "lessons", lessonId || ""),
      (doc) => {
        if (doc.data()?.isCancelled) {
          setUserWantsToDisconnect(true);
          disconnectFromRoom();
          window.location.href = "/cancelled";
        }
        const lesson = doc.data() as Lesson;
        setLessonInfo({
          ...lesson,
          id: doc.id
        } as Lesson);
      }
    );
    return () => unsubscribe();
  }, [lessonId]);

  const {
    localTracks,
    toggleAudio,
    toggleVideo,
    disconnectFromRoom,
    toggleScreenShare,
    isSharingScreen,
  } = useVideoContext();

  const onToggleWhiteboard = () => {
    setIsWhiteboardOpen(!isWhiteboardOpen);
  };

  const handleTranscriptionToggle = () => {
    setIsTranscribing(!isTranscribing);
  };

  const onLessonEnd = () => {
    if (userRole === UserRole.Student && lessonInfo?.type === "guest" && lessonId) {
      const currentDate = formatDateForFilename(new Date());
      downloadLessonNotesAsPDF(lessonId, currentDate, lessonInfo).catch((error) => {
        console.error('Failed to download lesson notes:', error);
      });
    }
    authenticatedAxios.post(`/lessons/end/${lessonId}`).then(() => {
      setUserWantsToDisconnect(true);
      disconnectFromRoom();
      window.location.href = `/${lessonId}/completed`;
    });
  };

  const isMicEnabled = localTracks.some((t) => t.kind === "audio");
  const handleToggleAudio = () => {
    toggleAudio();
    const newIsMicEnabled = !isMicEnabled;
    sessionStorage.setItem("initialMicEnabled", newIsMicEnabled.toString());
  };

  const isCameraEnabled = localTracks.some((t) => t.kind === "video");
  const handleToggleVideo = () => {
    toggleVideo();
    const newIsCameraEnabled = !isCameraEnabled;
    sessionStorage.setItem(
      "initialCameraEnabled",
      newIsCameraEnabled.toString()
    );
  };

  useEffect(() => {
    if (blocker.state === "blocked") {
      if (confirm("Are you sure you want to disconnect?")) {
        setUserWantsToDisconnect(true);
        disconnectFromRoom();
        blocker.proceed();
      }
    }
  }, [blocker]);

  return (
    <ValidationErrorProvider>
      <GlobalValidationError />
      <TimerSnackbar lessonInfo={lessonInfo} isSandbox={isSandbox} />
      <Stack
        direction="column"
        sx={{
          height: "100vh",
          width: "100vw",
          overflow: "hidden"
        }}
      >
        <ConferenceHeader
          userName={userName}
          userPhoto={userPhoto}
          userId={userId}
          lessonInfo={lessonInfo}
          lessonId={lessonId}
          onLessonEnd={onLessonEnd}
          onProceed={onProceed}
          isSandbox={isSandbox}
        />
        <Stack
          flex={1}
          py={1}
          px={2}
          direction="row"
          spacing={2}
          sx={{
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          <Stack
            direction="column"
            spacing={2}
            flex={1}
            sx={{
              minHeight: 0,
              overflow: "hidden"
            }}
          >
            <Stack direction="row" spacing={2} flex={1} sx={{ minHeight: 0 }}>
              <VideoArea
                roomToken={roomToken}
                isWhiteboardOpen={isWhiteboardOpen}
                userRole={userRole}
                isTranscribing={isTranscribing}
                isLessonCompleted={lessonInfo?.hasCompleted || false}
                onToggleWhiteboard={onToggleWhiteboard}
                userWantsToDisconnect={userWantsToDisconnect}
                isSandbox={isSandbox}
              />
              <Sidebar
                userRole={userRole}
                sidebarState={sidebarState}
                setSidebarState={setSidebarState}
                lessonType={lessonInfo?.type || "standard"}
                isSandbox={isSandbox}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              width="100%"
              justifyContent="center"
              sx={{ flexShrink: 0 }}
            >
              <VideoControls
                isMicEnabled={isMicEnabled}
                isCameraEnabled={isCameraEnabled}
                onToggleAudio={handleToggleAudio}
                onToggleVideo={handleToggleVideo}
                isScreenSharing={isSharingScreen}
                onToggleScreenShare={toggleScreenShare}
                onDisconnect={() => {
                  navigate(`/${lessonId}`);
                }}
                isTranscribing={isTranscribing}
                onToggleTranscription={handleTranscriptionToggle}
                onToggleWhiteboard={onToggleWhiteboard}
                isSandbox={isSandbox}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </ValidationErrorProvider>
  );
};

export default Conference;
